<template>
  <div>
    <Loading :isActive="loading"></Loading>

    <!--
      Carga que falhou não pode passar por tela em branco.

      Os campos desta tela nascem vazios em `data()`, e o salvar manda todos
      eles de uma vez. Sem este aviso, quem abrisse depois de um erro de rede
      veria os prompts em branco, concluiria que nunca foram escritos e, ao
      salvar, apagaria os que estão gravados.
    -->
    <CAlert v-if="erroCarregamento" show color="danger" class="alerta-carga">
      <div>{{ erroCarregamento }}</div>
      <CButton color="danger" variant="outline" size="sm" @click="loadParametro">
        Tentar novamente
      </CButton>
    </CAlert>

    <CTabs variant="tabs" class="nav-tabs-boxed" :active-tab.sync="activeTab">
      <CTab title="Conexão">
        <TabConexao
          :parametro="parametro"
          @update="onUpdateParametro"
        />
      </CTab>

      <CTab title="Prompts">
        <TabPrompts
          :parametro="parametro"
          @update="onUpdateParametro"
        />
      </CTab>

      <CTab title="Avaliação de atendimentos">
        <TabAvaliacaoAtendimento
          :parametro="parametro"
          @update="onUpdateParametro"
        />
      </CTab>

      <CTab title="Dicionário">
        <TabDicionario />
      </CTab>

      <CTab title="Pré-Prompts">
        <TabPrePrompts />
      </CTab>

      <CTab title="Templates de análise">
        <div class="tab-templates-wrapper">
          <div class="info-alert">
            <CIcon name="cil-info" class="info-icon" />
            <div>
              <strong>Templates de Análise:</strong> modelos de prompt que definem como a IA deve estruturar resumos e análises de dados.
              Cada template contém instruções que orientam a IA sobre formato, tom e profundidade da análise gerada (ex: resumo de vendas, análise de desempenho, relatório de clientes inativos).
            </div>
          </div>
          <crud-base
            :url="templateUrl"
            :columns="templateColumns"
            :onDelete="() => {}"
            :onUpdate="() => {}"
            :onStore="() => {}"
            :enableSearch="true"
            :checkAlerts="true"
            primaryKey="id"
          />
        </div>
      </CTab>

      <CTab title="Log de Utilização">
        <TabLogUtilizacao />
      </CTab>
    </CTabs>

    <div class="footer-actions" v-if="abaEditaParametro">
      <CButton
        color="primary"
        :disabled="saving || !parametroCarregado"
        :title="parametroCarregado ? '' : 'A configuração ainda não foi carregada.'"
        @click="salvarParametro"
      >
        {{ saving ? "Salvando..." : "Salvar Configurações" }}
      </CButton>
    </div>
  </div>
</template>

<script>
import { get, put } from "@/helpers/apiRequest";
import CrudBase from "../../components/crud/CrudBase";
import TabConexao from "./TabConexao.vue";
import TabPrompts from "./TabPrompts.vue";
import TabAvaliacaoAtendimento from "./TabAvaliacaoAtendimento.vue";
import TabDicionario from "./TabDicionario.vue";
import TabPrePrompts from "./TabPrePrompts.vue";
import TabLogUtilizacao from "./TabLogUtilizacao.vue";

/*
 * O que esta tela edita.
 *
 * Prompts, critérios e interruptores são regra de negócio e vivem na
 * integração, ao lado da credencial e dos modelos — antes ficavam no
 * parâmetro, por herança de quando tudo era uma chave só. O endpoint abaixo
 * grava só estes campos, então a tela não precisa mais devolver o lado técnico
 * junto para não perdê-lo.
 */
const ENDPOINT = "/admin/configuracao-ia";

const CAMPOS_DA_TELA = [
  "prompt_sugestao_pedido",
  "prompt_contexto_negocio",
  "avaliar_atendimento_ia",
  "criterios_avaliacao_atendimento",
  "prompt_avaliacao_atendimento",
  "modo_aprendizado",
];

export default {
  name: "configuracao-ia",
  components: {
    CrudBase,
    TabConexao,
    TabPrompts,
    TabAvaliacaoAtendimento,
    TabDicionario,
    TabPrePrompts,
    TabLogUtilizacao,
  },
  data() {
    return {
      loading: false,
      saving: false,
      // O salvar envia CAMPOS_DA_TELA inteiro, então só pode valer depois de
      // uma leitura que deu certo — senão grava os defaults vazios por cima.
      parametroCarregado: false,
      erroCarregamento: "",
      activeTab: 0,
      templateUrl: "/admin/template-resumo-ia",
      templateColumns: [
        { label: "Id", field: "id", sortable: false },
        { label: "Nome", field: "nome", sortable: false },
        { label: "Tipo", field: "tipo", sortable: false },
        { label: "Cadastrado por", field: "autor_nome", sortable: false },
      ],
      parametro: {
        prompt_sugestao_pedido: "",
        prompt_contexto_negocio: "",
        // Avaliação de atendimentos (ligação e WhatsApp): o interruptor liga a
        // avaliação; os critérios dizem o QUE avaliar.
        avaliar_atendimento_ia: false,
        criterios_avaliacao_atendimento: [],
        prompt_avaliacao_atendimento: "",
        modo_aprendizado: false,
      },
    };
  },
  computed: {
    /*
     * As abas que mexem no parâmetro. As demais — dicionário, pré-prompts,
     * templates e log — gravam por conta própria, e o botão ali prometeria
     * salvar algo que não está em edição.
     */
    abaEditaParametro() {
      return this.activeTab <= 2;
    },
  },
  methods: {
    loadParametro() {
      this.loading = true;
      this.erroCarregamento = "";

      return get(ENDPOINT)
        .then((resposta) => {
          const valor = (resposta && resposta.data) || {};

          CAMPOS_DA_TELA.forEach((campo) => {
            if (valor[campo] !== undefined && valor[campo] !== null) {
              this.parametro[campo] = valor[campo];
            }
          });

          this.parametroCarregado = true;
          this.loading = false;
        })
        .catch((erro) => {
          this.parametroCarregado = false;
          this.erroCarregamento = (erro && erro.message)
            || "Não foi possível carregar a configuração de IA. Salvar está bloqueado "
            + "para não apagar o que já está gravado.";
          this.loading = false;
        });
    },
    onUpdateParametro(changes) {
      this.parametro = { ...this.parametro, ...changes };
    },
    valorParaSalvar() {
      const daTela = {};

      CAMPOS_DA_TELA.forEach((campo) => {
        daTela[campo] = this.parametro[campo];
      });

      return daTela;
    },
    salvarParametro() {
      this.saving = true;
      put(ENDPOINT, this.valorParaSalvar())
        .then((json) => {
          this.saving = false;
          this.$swal({
            title: "Salvo com sucesso",
            text: json.message || "Configurações atualizadas.",
            icon: "success",
            showConfirmButton: true,
          });
        })
        .catch((error) => {
          this.saving = false;
          this.$swal({
            title: "Falha ao Salvar",
            text: error.message || "Erro ao salvar configurações.",
            icon: "error",
            showCancelButton: true,
            cancelButtonText: "Fechar",
            showConfirmButton: false,
          });
        });
    },
  },
  mounted() {
    this.loadParametro();
  },
};
</script>

<style scoped>
.alerta-carga {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.footer-actions {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid #e4e7eb;
  background: #fff;
}

.tab-templates-wrapper {
  padding: 20px;
}

.info-alert {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #344054;
  line-height: 1.5;
}

.info-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2298a3;
}
</style>
